// React
import { useEffect, useState, PropsWithChildren } from "react"

// Components
import { PrintTransaction } from "components"
import { TableJournalEntry } from "./Tables/JournalEntry"

// Form
import { useFormContext } from "react-hook-form"

// Third-Party Libraries
import moment from "moment"

// Types
import type { PaymentInvoiceType } from "../types"

// Utils
import { useCountryById, useHandleAuth, useProvinceById, useRegencyById, useSubdistrictById } from "utils"
import { useSubdomainSetting } from "pages/Purchase/Transaction/PurchaseOrder/utils"
import { useList } from "pages/Setup/Preference/SettingCompany/utils/hooks"
import { useCustomerDetail } from "../../CustomerOrder/utils/hooks"

export function Print(): JSX.Element {
  // Form
  const { formState: { defaultValues }, getValues } = useFormContext<PaymentInvoiceType>()
  
  // Hooks
  const company = useList()
  const country = useCountryById()
  const customer = useCustomerDetail(getValues('customer_id'))
  const province = useProvinceById()
  const regency = useRegencyById()
  const setting = useSubdomainSetting()
  const subdistrict = useSubdistrictById()
  const { userTokenData } = useHandleAuth()
  const [address, setAddress] = useState<string>("")

  // Vars
  const isLoading: boolean = company.isLoading || customer.isLoading || country.isLoading || province.isLoading || subdistrict.isLoading || regency.isLoading
  const userToken = userTokenData()

  useEffect(() => {
    company.fetchData().then((res) => {
      setAddress(res.address)

      if (res.subdistrict_id) {
        subdistrict.fetchData(res.subdistrict_id)
      }

      if (res.regency_id) {
        regency.fetchData(res.regency_id)
      }

      if (res.province_id) {
        province.fetchData(res.province_id)
      }

      if (res.country_id) {
        country.fetchData(res.country_id)
      }
    })

    // eslint-disable-next-line
  }, [])

  return (
    <PrintTransaction loading={isLoading}>
      <section className="flex flex-col gap-3 text-[14px]">
        <div className="text-center text-2xl font-bold uppercase">
          {userToken?.company_name}
        </div>

        <section className="grid grid-cols-2 items-stretch gap-1">
          <section className="font-bold">
            <table className="table-top">
              <tbody className="font-bold">
                <InfoItems  uppercase label="ADDRESS" text={address} />
                <InfoItems  label="PHONE" text={setting.data?.phone!} />
                <InfoItems  label="EMAIL" text={setting.data?.email!} />
                <InfoItems  label="WEBSITE" text={setting.data?.website!} />
              </tbody>
            </table>
          </section>

          <section className="flex flex-col justify-between items-end">
            <div className="text-2xl font-bold text-right">
              PAYMENT INVOICE
            </div>

            <table>
              <tbody className="font-bold ">
                <InfoItems textWidth={200} textRight label="DATE" text={moment(getValues('transaction_date').toString()).format('DD-MM-YYYY') ?? "-"} />
                <InfoItems textWidth={200} textRight label="PAYMENT INVOICE NO" text={defaultValues?.trx_code!} />
              </tbody>
            </table>
          </section>
        </section>

        <hr />

        <table className="table table-bordered">
          <tbody>
            <tr className="bg-black text-white font-bold">
              <td colSpan={2}>CUSTOMER INFORMATION</td>
            </tr>

            <tr>
              <td>
                <div>NAME</div>
                <div className="font-bold">{customer.data?.customer_name}</div>
              </td>
              <td>
                <div>ADDRESS</div>
                <div className="font-bold">{customer.data?.address}</div>
              </td>
            </tr>

            <tr>
              <td>
                <div>CONTACT NO</div>
                <div className="font-bold">{customer.data?.handphone}</div>
              </td>

              <td>
                <div>EMAIL ADDRESS</div>
                <div className="font-bold">{customer.data?.email}</div>
              </td>
            </tr>
          </tbody>
        </table>

        <hr />

        <TableJournalEntry key={1}/>
      </section>
      <hr className="my-[10px]"/>
      <section className="grid grid-cols-5 items-stretch gap-5">
          <section className="col-span-2">
            <table className="table table-bordered">
              <tbody>
                <tr className="bg-black text-white font-bold">
                  <td>Additional Notes:</td>
                </tr>

                <tr className="min-h-[300px]">
                  <td>{defaultValues?.memo}</td>
                </tr>
              </tbody>
            </table>
          </section>
          <div />

          <section className="col-span-2">
            <table className="w-full">
              <tbody>
                <tr className="bg-black text-white font-bold">
                  <td>Authorized By</td>
                </tr>

                <tr className="h-[100px]" />

                <tr>
                  <td>{defaultValues?.approve_by_name}</td>
                </tr>
              </tbody>
            </table>
          </section>
        </section>
    </PrintTransaction>
  )
}

type InfoItemsType = {
  label: string
  text: string | number
  uppercase?: boolean
  textRight?: boolean
  width?: number
  textWidth?: number
}
const InfoItems = (props: PropsWithChildren<InfoItemsType>) => (
  <tr>
    <td width={props?.width ? props?.width : 120} className="pr-2 uppercase flex justify-between">
      <div>{props?.label}</div>
      <div>:</div>
    </td>
    <td width={props?.textWidth} className={props?.uppercase ? `uppercase` : props?.textRight ? "text-right" : ""}>{props?.text || "-"}</td>
  </tr>
)